import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Zap, Target, Shield, ExternalLink, Info, HelpCircle } from 'lucide-react';
import { useTopOpportunities } from '@/hooks/useTopOpportunities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking';
import { WhaleFlowBadge } from '@/components/WhaleFlowIndicator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const TossWidget = () => {
  const { data: opportunities, isLoading } = useTopOpportunities({
    limit: 10,
    minScore: 30, // Lowered to show best available tokens in $10M-$1B range
  });
  const { trackAffiliateClick } = useAffiliateTracking();
  const [showAll, setShowAll] = useState(false);

  const displayedOpportunities = showAll ? opportunities : opportunities?.slice(0, 5);

  const getRatingColor = (rating: string | undefined) => {
    switch (rating) {
      case 'HOT_PICK': return 'from-red-500 via-orange-500 to-yellow-500';
      case 'STRONG_CONTENDER': return 'from-green-500 to-emerald-400';
      case 'WATCHLIST': return 'from-blue-500 to-cyan-400';
      default: return 'from-gray-500 to-gray-400';
    }
  };

  const getRatingEmoji = (rating: string | undefined) => {
    switch (rating) {
      case 'HOT_PICK': return '🔥';
      case 'STRONG_CONTENDER': return '⭐';
      case 'WATCHLIST': return '👀';
      default: return '•';
    }
  };

  const getRatingLabel = (rating: string | undefined) => {
    return rating?.replace('_', ' ') || 'N/A';
  };

  const handleBuyClick = (platform: 'swyftx' | 'coinspot', symbol: string) => {
    trackAffiliateClick(platform);
    const urls = {
      swyftx: 'https://trade.swyftx.com.au/register/?ref=sydneygotoman',
      coinspot: 'https://www.coinspot.com.au?affiliate=6XWLW'
    };
    window.open(urls[platform], '_blank');
  };

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-card/80 via-card/60 to-card/80 backdrop-blur border-primary/20">
        <CardHeader>
          <CardTitle className="text-3xl font-bold flex items-center gap-3">
            <Target className="h-8 w-8 text-primary" />
            Token Opportunity Scoring System
          </CardTitle>
          <CardDescription className="text-base">
            TOSS analyzes $10M-$1B market cap tokens with 10X potential
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!opportunities || opportunities.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-card/80 via-card/60 to-card/80 backdrop-blur border-primary/20">
        <CardHeader>
          <CardTitle className="text-3xl font-bold flex items-center gap-3">
            <Target className="h-8 w-8 text-primary" />
            Token Opportunity Scoring System
          </CardTitle>
          <CardDescription>
            No opportunities found. Run the enhanced market analysis to generate TOSS scores.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-card/80 via-card/60 to-card/80 backdrop-blur border-primary/20">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-3xl font-bold flex items-center gap-3 mb-2">
              <Target className="h-8 w-8 text-primary animate-pulse" />
              Token Opportunity Scoring System (TOSS)
            </CardTitle>
            <CardDescription className="text-base">
              AI-powered scoring for $10M-$1B market cap tokens with 10X potential on Australian exchanges
            </CardDescription>
            <div className="flex gap-2 mt-3">
              <Badge variant="secondary" className="gap-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
                </span>
                Live Analysis
              </Badge>
              <Badge variant="outline">{opportunities.length} Opportunities</Badge>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Info className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p className="font-semibold mb-2">TOSS Scoring Breakdown:</p>
                <p className="text-xs text-muted-foreground mb-2">Base score out of 100, plus up to +15 Social Boost (total can reach 115).</p>
                <ul className="space-y-1 text-sm">
                  <li>• Momentum (30pts): Price & volume analysis</li>
                  <li>• Position (20pts): Market cap & exchange availability</li>
                  <li>• Technical (20pts): RSI, MA, support/resistance</li>
                  <li>• Fundamentals (15pts): Development & on-chain metrics</li>
                  <li>• Sentiment (10pts): Social buzz & community</li>
                  <li>• Risk (5pts): Liquidity & project age</li>
                  <li>• Social Boost (+0 to +15, additive): Narrative divergence & spiking social volume</li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Link
            to="/how-toss-works"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline ml-2 shrink-0"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            How TOSS works
          </Link>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {displayedOpportunities?.map((opp, index) => {
            // Determine rating based on adjusted score thresholds
            let rating = 'SKIP';
            if (opp.opportunity_score >= 65) rating = 'HOT_PICK';
            else if (opp.opportunity_score >= 55) rating = 'STRONG_CONTENDER';
            else if (opp.opportunity_score >= 45) rating = 'WATCHLIST';
            
            return (
              <div 
                key={opp.id}
                className="relative p-6 rounded-xl border-2 border-border/50 bg-gradient-to-br from-background/90 to-background/50 hover:border-primary/50 transition-all group"
              >
                {/* Rating Badge */}
                <div className="absolute -top-3 -right-3">
                  <div className={`bg-gradient-to-r ${getRatingColor(rating)} px-4 py-1 rounded-full text-sm font-bold text-white shadow-lg flex items-center gap-1`}>
                    <span>{getRatingEmoji(rating)}</span>
                    <span>{getRatingLabel(rating)}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-4">
                  {/* Rank & Score */}
                  <div className="flex flex-col items-center justify-center min-w-[80px] p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <span className="text-sm text-muted-foreground font-semibold">RANK</span>
                    <span className="text-4xl font-bold text-primary">#{index + 1}</span>
                    <div className="mt-2 text-center">
                      <span className="text-2xl font-bold text-foreground">{Math.round(opp.opportunity_score)}</span>
                      <span className="text-xs text-muted-foreground block">/ 100</span>
                    </div>
                  </div>

                  {/* Token Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-foreground">{opp.token_symbol}</h3>
                      <span className="text-sm text-muted-foreground">({opp.token_name})</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-semibold text-foreground">
                          {opp.narrative || 'Market Opportunity'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Risk:</span>
                        <Badge variant={
                          opp.risk_level === 'low' ? 'default' : 
                          opp.risk_level === 'medium' ? 'secondary' : 'destructive'
                        }>
                          {opp.risk_level?.toUpperCase()}
                        </Badge>
                      </div>

                      {/* Whale Flow Indicator */}
                      <WhaleFlowBadge 
                        symbol={opp.token_symbol}
                        volume24h={opp.volume_24h}
                        marketCap={opp.market_cap}
                        priceChange24h={opp.price_change_24h ?? undefined}
                        priceChange7d={opp.price_change_7d ?? undefined}
                      />

                      <div className="flex items-center gap-2">
                        <span className="text-lg font-mono font-semibold text-foreground">
                          ${opp.current_price.toFixed(opp.current_price < 1 ? 4 : 2)}
                        </span>
                        {opp.price_change_24h !== null && (
                          <Badge variant={opp.price_change_24h >= 0 ? 'default' : 'destructive'}>
                            {opp.price_change_24h >= 0 ? '+' : ''}{opp.price_change_24h.toFixed(2)}%
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Score Breakdown */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                      {[
                        { label: 'Momentum', value: opp.score_price_action, max: 30 },
                        { label: 'Position', value: opp.score_volume, max: 20 },
                        { label: 'Technical', value: opp.score_technicals, max: 20 },
                        { label: 'Fundamentals', value: opp.score_fundamentals, max: 15 },
                        { label: 'Sentiment', value: opp.score_sentiment, max: 10 },
                        { label: 'Risk', value: opp.score_risk_adjusted, max: 5 },
                      ].map((score) => (
                        <div key={score.label} className="text-center">
                          <div className="text-xs text-muted-foreground mb-1">{score.label}</div>
                          <div className="relative">
                            <Progress 
                              value={((score.value || 0) / score.max) * 100} 
                              className="h-2"
                            />
                            <span className="text-xs font-bold text-foreground">
                              {score.value?.toFixed(0) || 0}/{score.max}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {opp.position_details?.supplyValueHealth && (
                      <details className="mb-3 rounded-lg border border-border/50 bg-muted/20 px-3 py-2">
                        <summary className="cursor-pointer text-xs font-semibold text-foreground">
                          Supply & Value Health
                        </summary>
                        <div className="mt-3 grid gap-3 sm:grid-cols-3">
                          {[
                            { label: 'FDV Ratio', value: opp.position_details.supplyValueHealth.fdvRatioScore ?? 5, tooltip: 'Percentage of total supply already in circulation. Higher means less future dilution risk.' },
                            { label: 'Supply Circulation', value: opp.position_details.supplyValueHealth.supplyCirculationScore ?? 5, tooltip: 'How much of the maximum token supply is currently circulating.' },
                            { label: 'ATH Recovery Signal', value: opp.position_details.supplyValueHealth.athRecoveryScore ?? 5, tooltip: 'Tokens significantly below all-time high with rising volume may be in early repricing phase.' },
                          ].map((signal) => (
                            <div key={signal.label} className="space-y-1" title={signal.tooltip}>
                              <div className="flex items-center justify-between gap-2 text-xs">
                                <span className="text-muted-foreground">{signal.label}</span>
                                <span className="font-mono font-semibold text-foreground">{signal.value.toFixed(1)}/10</span>
                              </div>
                              <Progress value={(signal.value / 10) * 100} className="h-1.5" />
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Buy Signals */}
                    {opp.buy_signals && opp.buy_signals.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {opp.buy_signals.slice(0, 4).map((signal, idx) => (
                          <Badge 
                            key={idx}
                            variant="secondary"
                            className="text-xs"
                            title={signal.description}
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            {signal.type}
                          </Badge>
                        ))}
                        {opp.buy_signals.length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{opp.buy_signals.length - 4} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <Button 
                      size="sm"
                      className="w-full bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                      onClick={() => handleBuyClick('swyftx', opp.token_symbol)}
                    >
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Buy Swyftx
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => handleBuyClick('coinspot', opp.token_symbol)}
                    >
                      Buy CoinSpot
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {opportunities.length > 5 && (
          <div className="mt-6 text-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Show Less' : `Show All ${opportunities.length} Opportunities`}
            </Button>
          </div>
        )}

        <div className="mt-6 text-center">
          <Button 
            variant="default"
            size="lg"
            onClick={() => window.location.href = '/opportunities'}
          >
            View Detailed Analysis
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
